import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { useNotificationPermission } from '../../hooks/useNotificationPermission';
import Button from '../ui/button/Button';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { useAuth } from '../../context/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { t } from 'i18next';

interface NotificationPermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationPermissionModal({
  isOpen, onClose }: NotificationPermissionModalProps) {
  const { t } = useTranslation()
  const { requestPermission, loading } = useNotificationPermission();
  const { user } = useAuth();
  const [requesting, setRequesting] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const [step, setStep] = useState<1 | 2>(1);
  const [notificationDone, setNotificationDone] = useState(false);
  const [notificationGranted, setNotificationGranted] = useState(false);
  const [micDone, setMicDone] = useState(false);
  const [micGranted, setMicGranted] = useState(false);

  if (!isOpen) return null;

  const handleRequestMicPermission = async () => {
    if (requesting) return;
    setRequesting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      stream.getTracks().forEach((tr) => tr.stop());
      setMicGranted(true);
      setMicDone(true);
    } catch (e) {
      setMicGranted(false);
      setMicDone(true);
    } finally {
      setRequesting(false);
    }
  };

  const handleEnableNotifications = async () => {
    if (requesting || loading) return; // Prevenir clicks múltiples
    
    setRequesting(true);
    
    try {
      if (isNative) {
        if (Capacitor.getPlatform() === 'ios') {
          const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
          const permissionResult = await FirebaseMessaging.requestPermissions();

          if (permissionResult.receive === 'granted') {
            const res = await FirebaseMessaging.getToken();
            const token = res?.token;
            if (user?.uid && token) {
              await updateDoc(doc(db, 'users', user.uid), {
                fcmToken: token,
                fcmTokenUpdatedAt: new Date(),
                notificationPermissionGranted: true,
              });
            }
          }

          setNotificationGranted(permissionResult.receive === 'granted');
        } else {
          // Android: Usar permisos nativos de Capacitor
          const permissionResult = await PushNotifications.requestPermissions();
          
          if (permissionResult.receive === 'granted') {
            // Registrar para recibir notificaciones
            await PushNotifications.register();
            
            // Escuchar el token
            PushNotifications.addListener('registration', async (token) => {
              if (user?.uid) {
                // Guardar token nativo en Firestore
                await updateDoc(doc(db, 'users', user.uid), {
                  fcmToken: token.value,
                  fcmTokenUpdatedAt: new Date(),
                  notificationPermissionGranted: true,
                });
              }
            });
            
            // Escuchar errores de registro
            PushNotifications.addListener('registrationError', (error) => {
              console.error('Error en registro de notificaciones:', error);
            });
          }

          setNotificationGranted(permissionResult.receive === 'granted');
        }

        setNotificationDone(true);
        setStep(2);
        setRequesting(false);
        return;
      } else {
        // Web: Usar fcmService para FCM completo
        import('../../services/fcmService').then(async (module) => {
          const fcmService = module.default;
          if (user?.uid) {
            await fcmService.requestPermissionAndGetToken(user.uid);
          }
        });

        setNotificationGranted(Notification.permission === 'granted');
        setNotificationDone(true);
        setStep(2);
        setRequesting(false);
      }
    } catch (error) {
      console.error('Error al activar notificaciones:', error);
      setRequesting(false);
      setNotificationGranted(false);
      setNotificationDone(true);
      setStep(2);
    }
  };

  const handleSkipNotifications = () => {
    setNotificationGranted(false);
    setNotificationDone(true);
    setStep(2);
  };

  const handleContinue = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            {step === 1
              ? t('common.no tepierdas nada')
              : t('calls.permissions.micTitle', { defaultValue: 'Permisos de micrófono' })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Icono */}
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-[#AAD08C]/20 rounded-full flex items-center justify-center">
              {step === 1 ? (
                <svg className="w-8 h-8 text-[#055590]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-[#055590]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              )}
            </div>
          </div>

          {/* Descripción */}
          <p className="text-sm text-gray-600 text-center">
            {step === 1
              ? t('common.activalasnotificacionespararecibirrecordatorios')
              : t('calls.permissions.micDesc', { defaultValue: 'Se usan solo para realizar llamadas. No grabamos audio.' })}
          </p>

          {/* Beneficios */}
          <div className="space-y-2">
            {step === 1 ? (
              <>
                <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded-lg">
                  <svg className="w-4 h-4 text-[#AAD08C]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('common.recordatoriosdeactividades')}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded-lg">
                  <svg className="w-4 h-4 text-[#AAD08C]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('common.actualizacionesfamiliares')}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded-lg">
                  <svg className="w-4 h-4 text-[#AAD08C]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('common.celebralogros')}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-gray-700 p-2 bg-gray-50 rounded-lg">
                <svg className="w-4 h-4 text-[#AAD08C]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {t('calls.permissions.micBenefit', { defaultValue: 'Necesario para hablar durante la llamada' })}
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="space-y-3 pt-2">
            {step === 1 ? (
              <>
                <button
                  onClick={handleEnableNotifications}
                  disabled={requesting || loading}
                  className="h-[40px] rounded-full px-4 font-black bg-[#055590] hover:bg-[#055590]/90 text-white hover:shadow-md shadow-sm transition-all active:scale-[0.98] flex items-center justify-center gap-1.5 select-none cursor-pointer shrink-0 w-full"
                >
                  {requesting || loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('common.activando')}
                    </span>
                  ) : notificationDone ? (
                    notificationGranted
                      ? `✅ ${t('common.activarnotificaciones')}`
                      : `⏭️ ${t('common.ahorano')}`
                  ) : (
                    '🔔' + t('common.activarnotificaciones')
                  )}
                </button>

                <button
                  onClick={handleSkipNotifications}
                  className="h-[40px] rounded-full px-4 font-black text-xs text-gray-600 hover:bg-gray-50 transition-all flex items-center justify-center gap-1.5 w-full"
                >
                  {t('common.ahorano')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={async () => {
                    if (micDone) {
                      handleContinue();
                      return;
                    }
                    await handleRequestMicPermission();
                  }}
                  disabled={requesting}
                  className="h-[40px] rounded-full px-4 font-black bg-[#055590] hover:bg-[#055590]/90 text-white hover:shadow-md shadow-sm transition-all active:scale-[0.98] flex items-center justify-center gap-1.5 select-none cursor-pointer shrink-0 w-full"
                >
                  {requesting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('common.activando')}
                    </span>
                  ) : micDone ? (
                    t('common.continuar', { defaultValue: 'Continuar' })
                  ) : (
                    t('common.siguiente', { defaultValue: 'Siguiente' })
                  )}
                </button>

                {micDone && !micGranted && (
                  <div className="text-xs text-gray-500 text-center">
                    {t('calls.permissions.micDenied', { defaultValue: '❌ Micrófono denegado' })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Nota de privacidad */}
          <p className="text-xs text-gray-500 text-center">
            {step === 1
              ? t('common.puedesactivarlasnotificacionesmastardeenpreferencias')
              : t('calls.permissions.micPrivacy', { defaultValue: 'Puedes activar el micrófono más tarde desde los permisos del sistema.' })}
          </p>
        </div>
      </div>
    </div>
  );
}
